import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import supabase from '../utils/supabaseClient';
import { clearAuthCookies } from '../utils/cookieUtils';

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  const bootstrapProfile = useCallback(async (currentUser) => {
    if (!currentUser) return null;

    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      const localProfile = {
        id: currentUser.id,
        email: currentUser.email ?? '',
        display_name:
          currentUser.user_metadata?.display_name || currentUser.email?.split('@')[0] || '',
        updated_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
      };
      return localProfile;
    }

    const now = new Date().toISOString();
    const baseProfile = {
      id: currentUser.id,
      email: currentUser.email ?? '',
      display_name:
        currentUser.user_metadata?.display_name || currentUser.email?.split('@')[0] || '',
      updated_at: now,
      created_at: now,
    };

    const { data, error } = await supabase
      .from('profiles')
      .upsert(baseProfile, { onConflict: 'id' })
      .select()
      .maybeSingle();

    if (error) {
      console.error('[AuthContext] Failed to bootstrap profile:', error);
      return baseProfile;
    }

    return data ?? baseProfile;
  }, []);

  const loadProfile = useCallback(async (currentUser) => {
    if (!currentUser) {
      setProfile(null);
      setProfileLoading(false);
      return null;
    }

    setProfileLoading(true);

    try {
      let sessionToken = null;
      
      // Check localStorage for session token
      const allKeys = Object.keys(localStorage);
      const supabaseKeys = allKeys.filter(k => k.includes('supabase') || k.includes('sb-') || k.includes('auth'));
      
      const possibleKeys = [
        'sb-kaewlwlaumfewzmbstdo-auth-token',
        'supabase.auth.token',
        ...supabaseKeys
      ];
      
      for (const key of possibleKeys) {
        const storedSession = localStorage.getItem(key);
        if (storedSession) {
          try {
            const parsed = JSON.parse(storedSession);
            sessionToken = parsed?.access_token || parsed?.currentSession?.access_token;
            if (sessionToken) break;
          } catch (e) {
            // Skip invalid JSON
          }
        }
      }
      
      // Fallback to supabase.auth.getSession()
      if (!sessionToken) {
        try {
          const sessionPromise = supabase.auth.getSession();
          const sessionTimeout = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Session fetch timeout')), 2000)
          );
          const { data: sessionData } = await Promise.race([sessionPromise, sessionTimeout]);
          sessionToken = sessionData?.session?.access_token;
        } catch (sessionError) {
          // Timeout or error - continue without token
        }
      }
      
      if (!sessionToken) {
        throw new Error('No authentication token available');
      }
      
      // Make REST API call with auth headers
      const headers = {
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${sessionToken}`,
        'Content-Type': 'application/json',
      };
      
      const response = await fetch(
        `https://kaewlwlaumfewzmbstdo.supabase.co/rest/v1/profiles?id=eq.${currentUser.id}&select=*`,
        { headers }
      );
      
      const data = await response.json();
      
      if (data && Array.isArray(data) && data.length > 0) {
        setProfile(data[0]);
        return data[0];
      } else if (data.message || data.error) {
        throw new Error(data.message || data.error || 'Failed to load profile');
      } else {
        const bootstrapped = await bootstrapProfile(currentUser);
        setProfile(bootstrapped);
        return bootstrapped;
      }
    } catch (err) {
      console.error('[AuthContext] Error loading profile:', err);
      const fallback = await bootstrapProfile(currentUser);
      setProfile(fallback);
      return fallback;
    } finally {
      setProfileLoading(false);
    }
  }, [bootstrapProfile]);

  useEffect(() => {
    let ignore = false;

    const applySession = async (nextSession) => {
      if (ignore) return;

      const nextUser = nextSession?.user ?? null;
      setSession(nextSession ?? null);
      setUser(nextUser);

      try {
        if (nextUser) {
          await loadProfile(nextUser);
        } else {
          setProfile(null);
          setProfileLoading(false);
        }
      } catch (err) {
        console.error('[AuthContext] Error in applySession:', err);
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    const initialiseSession = async () => {
      setLoading(true);
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error('[AuthContext] Failed to fetch session:', error);
      }

      await applySession(data?.session ?? null);
    };

    initialiseSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
      setLoading(true);
      await applySession(nextSession ?? null);
    });

    return () => {
      ignore = true;
      subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signIn = useCallback(async ({ email, password }) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      console.error('[AuthContext] Sign in error:', error);
      throw error;
    }
    return data;
  }, []);

  const signUp = useCallback(async ({ email, password, displayName }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          display_name: displayName,
        },
      },
    });

    if (error) throw error;

    const newUser = data.user;
    if (newUser && data.session) {
      const now = new Date().toISOString();
      const bootstrap = {
        id: newUser.id,
        email,
        display_name: displayName || newUser.email?.split('@')[0] || '',
        created_at: now,
        updated_at: now,
      };

      const { error: profileError } = await supabase
        .from('profiles')
        .upsert(bootstrap, { onConflict: 'id' });
      if (profileError) {
        console.warn('[AuthContext] Failed to bootstrap profile:', profileError);
      }

      await loadProfile(newUser);
    }

    return data;
  }, [loadProfile]);

  const signOut = useCallback(async () => {
    setSession(null);
    setUser(null);
    setProfile(null);
    setLoading(false);
    setProfileLoading(false);

    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.warn('[AuthContext] Supabase signOut error:', error);
      }
    } catch (err) {
      console.warn('[AuthContext] Error during signOut:', err);
    }

    if (typeof window !== 'undefined') {
      const storage = window.localStorage;
      const prefixes = ['mathbox-auth', 'sb-', 'supabase.auth'];

      for (let i = storage.length - 1; i >= 0; i -= 1) {
        const key = storage.key(i);
        if (!key) continue;
        if (prefixes.some((prefix) => key.startsWith(prefix))) {
          storage.removeItem(key);
        }
      }

      clearAuthCookies();
    }
  }, []);

  const updateProfile = useCallback(async (updates) => {
    if (!user) throw new Error('Cannot update profile without an authenticated user.');

    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      throw new Error('Session expired. Please sign in again.');
    }

    const payload = {
      id: user.id,
      updated_at: new Date().toISOString(),
      email: user.email ?? null,
      ...updates,
    };

    const { data, error } = await supabase
      .from('profiles')
      .upsert(payload, { onConflict: 'id' })
      .select()
      .maybeSingle();

    if (error) throw error;

    if (data) {
      setProfile(data);
      return data;
    }

    setProfile((prev) => ({ ...(prev || {}), ...payload }));
    return payload;
  }, [user]);

  const refreshProfile = useCallback(async () => {
    if (user) {
      setProfileLoading(true);
      await loadProfile(user);
    }
  }, [loadProfile, user]);

  const value = useMemo(() => ({
    session,
    user,
    profile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    refreshProfile,
  }), [loading, profile, profileLoading, refreshProfile, session, signIn, signOut, signUp, updateProfile, user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
